/**
 * Format a paginated list compliant with standards
 */
export class PaginatedList<T> {
  public pagination: Pagination;

  public items: T[];

  /** Create a new PaginatedList object
   * @param list list of elements
   * @param pagination pagination related to the elements
   */
  constructor(list: T[], pagination: Pagination) {
    this.items = list;
    this.pagination = pagination;
  }
}

export class Pagination {
  private _perPage: number;
  private _page: number;
  private _total: number;

  /**
   * Create a pagination based on list indexes and total length
   * @param {number} firstIndex first index of the page
   * @param {number} lastIndex last index of the page
   * @param {number} total total length of elements (all pages)
   * @param {number} [pageSize] optional page size: forces a page size. This can be useful for last pages when they are not full.
   */
  public static fromPagination(
    firstIndex: number,
    lastIndex: number,
    total: number,
    pageSize?: number,
  ): Pagination {
    const _perPage = pageSize || lastIndex - firstIndex + 1;
    const _page = Math.floor((firstIndex + 1) / _perPage);
    return new Pagination(_perPage, _page, total);
  }

  public static empty(): Pagination {
    return new Pagination(0, -1, 0);
  }

  constructor(perPage: number, page: number, total: number) {
    this._page = page;
    this._perPage = perPage;
    this._total = total;
  }

  /**
   * number of elements displayed per page
   */
  get perPage() {
    return this._perPage;
  }

  /**
   * index of the current page, count start from 0 for the first page, -1 if there is no result
   */
  get page() {
    return this._page;
  }

  /**
   * total number of elements
   */
  get total() {
    return this._total;
  }

  /**
   * Calculate the first index for a given page
   * @param page starts from 0
   */
  public getFirstIndex(page: number): number {
    return page * this.perPage;
  }

  /**
   * Calculate the last index for a given page
   * @param page starts from 0
   */
  public getLastIndex(page: number): number {
    return Math.min((page + 1) * this.perPage, this._total) - 1;
  }

  /**
   * Whether a given page is the last one
   * @param page starts from 0
   */
  public isLastPage(page: number): boolean {
    return (
      this.getFirstIndex(page) <= this._total - 1 &&
      this._total - 1 <= this.getLastIndex(page)
    );
  }

  /**
   * Whether the pagination is empty
   */
  public isEmpty(): boolean {
    return this._perPage === 0;
  }
}
